import React, { useEffect, useState } from 'react';
import { State } from 'src/redux/reducer';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { takingSurvey } from 'src/redux/features/surveys/takingSurvey/actions';
import TakingSurveyGreeting from 'src/components/TakingSurveyGreeting';
import TakingSurveyContent from 'src/components/TakingSurveyContent';
import { SurveyStatusEnum, SurveyTypeEnum } from 'src/utils/enum';
import {
  takingSurveySelector,
  takingSurveyLoadedSelector,
  takingSurveyPendingSelector,
} from 'src/redux/features/surveys/takingSurvey/selectors';
import { ISurvey } from 'src/redux/features/surveys/takingSurvey/types';
import { Text } from '@consta/uikit/Text';
import { Badge } from '@consta/uikit/Badge';
import { IconCancel } from '@consta/uikit/IconCancel';
import { IconEye } from '@consta/uikit/IconEye';
import { Loader } from '@consta/uikit/Loader';
import styles from './styles.styl';

import cn from 'classnames/bind';
import TakingSurveyFinal from 'src/components/TakinSurveyFinal';
import { putTakingSurveyLoadedSelector } from 'src/redux/features/surveys/putTakingSurvey/selectors';

const cx = cn.bind(styles);

export interface TakingSurveyProps {
  takingSurvey: (hash: string) => void;
  survey: ISurvey;
  isSurveyPending: boolean;
  isSurveyLoaded: boolean;
  isSurveyReRejected?: boolean;
  isPutSurveyLoaded: boolean;
}

export interface RouteParams {
  hash: string;
  preview: string;
}

const TakingSurvey: React.FC<TakingSurveyProps> = ({
  takingSurvey,
  survey,
  isSurveyPending,
  isSurveyLoaded,
  isPutSurveyLoaded,
}) => {
  const { hash, preview = null } = useParams<RouteParams>();
  const [step, setStep] = useState<string>('greeting');

  const {
    name,
    questions,
    welcome_page_header,
    welcome_page_description,
    final_page_header,
    final_page_description,
    type,
  } = survey;

  let { status } = survey;

  useEffect(() => {
    takingSurvey(hash);
  }, []);

  const turnStep = (step: string) => {
    setStep(step);
  };

  if (preview) {
    status = SurveyStatusEnum.DRAFT;
  }

  let maxScore: number | null = null;

  if (type === SurveyTypeEnum.TEST) {
    questions.forEach(({ points }) => {
      maxScore += points;
    });
  }

  const badgeStatus =
    status === SurveyStatusEnum.DRAFT ? (
      <Badge
        icon={IconEye}
        size="l"
        status="normal"
        label="предпросмотр"
        view="stroked"
      />
    ) : status === SurveyStatusEnum.ENDED ? (
      <Badge icon={IconCancel} size="l" status="error" label="завершен" />
    ) : null;

  let display;

  switch (step) {
    case 'greeting':
      display = (
        <TakingSurveyGreeting
          title={welcome_page_header}
          description={welcome_page_description}
          status={status}
          turnStep={turnStep}
          maxScore={maxScore}
          type={type}
        />
      );
      break;
    case 'start': {
      display = (
        <TakingSurveyContent
          questions={questions}
          turnStep={turnStep}
          status={status}
          hash={hash}
          type={type}
        />
      );
      break;
    }
  }

  return (
    <div className={cx('TakingSurvey')}>
      {step === 'final' || isPutSurveyLoaded ? (
        <TakingSurveyFinal
          title={final_page_header}
          description={final_page_description}
          maxScore={maxScore}
          status={status}
        />
      ) : (
        <div className={cx('TakingSurvey__container')}>
          {isSurveyPending && <Loader />}
          {isSurveyLoaded && (
            <>
              <div className={cx('TakingSurvey__title')}>
                <Text size="xl" view="primary" weight="bold">
                  {name}
                </Text>
                {badgeStatus}
              </div>
              {display}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  takingSurvey: (hash: string) => dispatch(takingSurvey(hash)),
});

const mapStateToProps = (state: State, props: any) => ({
  survey: takingSurveySelector(state),
  isSurveyPending: takingSurveyPendingSelector(state),
  isSurveyLoaded: takingSurveyLoadedSelector(state, props),
  isPutSurveyLoaded: putTakingSurveyLoadedSelector(state, props),
});

export default connect(mapStateToProps, mapDispatchToProps)(TakingSurvey);
